import { Modal } from "bootstrap";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef,  useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import uuid from "react-uuid";

import DownloadFile from "../../../services/DownloadFile";
import TapIcon from "../../../services/TapIcon";
import { HRMS_EMPLOYEE_URL } from "../../../services/TapApiUrls";
import api from "../../../services/api";


const INITIAL_STATE                 =   {
    loading                             :   false,
    submitting                          :   false,
};

const BulkEmployeeEdit              =   forwardRef((props, ref) => {

    const accessToken                   =   useSelector((state) => state.auth.access_token);

    const [state, setState]             =   useState(INITIAL_STATE);

    const modalId                       =   useRef(uuid()).current;
    const modalElementRef               =   useRef(null);
    const modalRef                      =   useRef(null);
    const formRef                       =   useRef(null);

    //Api File URL
    const fileApiUrl                    =   `${HRMS_EMPLOYEE_URL}/edit_by_excel`;

    const disabled                      =   state.loading || state.submitting;

    //---------------------------------------------------------
    // Modal Initialization
    //---------------------------------------------------------
    useEffect(() => {
        if (!modalElementRef.current) return;

        modalRef.current = new Modal(modalElementRef.current, { keyboard: false, backdrop: false });

        return () => {
            modalRef.current?.dispose();
        };
    }, []);

    //---------------------------------------------------------
    // Modal Controls
    //---------------------------------------------------------
    const openModal                     =   useCallback(() => {
        formRef.current?.reset();       // Clear selected file
        setState(INITIAL_STATE);        // Reset state
        modalRef.current?.show();       // Show modal
    }, []);

    const closeModal                    =   useCallback(() => {
        formRef.current?.reset();       // Clear selected file
        setState(INITIAL_STATE);        // Reset state
        modalRef.current?.hide();       // Hide modal
    }, []);

    useImperativeHandle(ref, () => ({
        modalHandle: openModal,
        initModalClose: closeModal,
    }));

    //---------------------------------------------------------
    // Download Sample
    //---------------------------------------------------------
    const handleDownloadSample          =   useCallback(async () => {
        try {
            setState((pd) => ({ ...pd, loading: true }));
            const response                  =   await api.server2Api.get(fileApiUrl, { params: { action: "sample" }, headers: { Authorization: `Bearer ${accessToken}`}});

            DownloadFile.file(response.data.file_path);

            toast.success("File downloaded successfully");

            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setState((pd) => ({ ...pd, loading: false }));
        }
    }, [accessToken, closeModal]);

    //---------------------------------------------------------
    // Upload File
    //---------------------------------------------------------
    const handleSubmit                  =   useCallback(
        async (e) => {
            e.preventDefault();
            if (!formRef.current) return;
            try {
                setState((pd) => ({ ...pd, submitting: true }));
                const formData          =   new FormData(formRef.current);
                const response          =   await api.server2Api.post(fileApiUrl,formData,{ params: { action: "save" }, headers :{Authorization: `Bearer ${accessToken}`} });
                toast.success(response.data.message);
                closeModal();
                if (response.data.generated_file?.file_path) {
                    DownloadFile.file(response.data.generated_file.file_path);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setState((pd) => ({ ...pd, submitting: false }));
            }
        },
        [accessToken, closeModal]
    );

    return <div className="modal fade" id={modalId} tabIndex="-1" ref={modalElementRef} >
        <div className="modal-dialog">
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">Bulk Edit Employee</h5>
                    <button type="button" className="btn-close" disabled={disabled} data-bs-dismiss="modal" />
                </div>
                <div className="modal-body">
                    <form ref={formRef} id={`${modalId}Form`} onSubmit={handleSubmit} >
                        <div className="row mt-3 mb-3">
                            <div className="col-12">
                                <input name="document" type="file" className="form-control" required disabled={disabled} />
                            </div>
                            <div className="col-12 mt-2 form-text">Excel formats: xls, xlm, xlsx, ods</div>
                            <div className="col-12 form-text">Maximum file size: 50 MB</div>
                            <div className="col-12 form-text">Maximum 1000 Fuel Feed In records</div>
                        </div>
                    </form>
                </div>

                <div className="modal-footer justify-content-between">
                    <Link onClick={handleDownloadSample} className={ disabled ? "disabled-link" : "" } >Download Sample File</Link>
                    <div>
                        <button type="button" className="btn btn-secondary mx-2" disabled={disabled} data-bs-dismiss="modal" >
                            Close
                        </button>
                        <button type="submit" form={`${modalId}Form`} disabled={disabled} className="btn btn-primary">
                            Submit{" "} {state.submitting && <TapIcon.FontAwesomeIcon icon={TapIcon.faSyncAlt} className="fa-spin"/>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>;
});

BulkEmployeeEdit.displayName = "BulkEmployeeEdit";

export default BulkEmployeeEdit;